import * as React from "react";
import { graphql, compose } from "react-apollo";
import gql from "graphql-tag";
import { Grid } from "@material-ui/core";

import { User } from "../../userTypes";
import ManualVerificationInitiateButton from "./ManualVerificationInitiateButton";
import ManualVerificationAcceptButton from "./ManualVerificationAcceptButton";
import ManualVerificationRejectButton from "./ManualVerificationRejectButton";
import ManualVerificationViewIdPhotoButton from "./ManualVerificationViewIdPhotoButton";
import ManualVerificationDownloadAllImagesButton from "./ManualVerificationDownloadAllImagesButton";
import ManualVerificationUploadDocumentButton from "./ManualVerificationUploadDocumentButton";
import ManualVerificationUploadDocumentModal from "./ManualVerificationUploadDocumentModal";
import ManualVerificationStatusChangeConfirmationModal from "./ManualVerificationStatusChangeConfirmationModal";
import ManualVerificationResetButton from "./ManualVerificationResetButton";
import ManualVerificationResetConfirmationModal from "./ManualVerificationResetConfirmationModal";
import { ConditionCheck } from "./manualVerificationConditionCheck";

type Props = {
  external_reference: string;
  onUserChanged: (user: User) => void;
  user: User;
  updateManualVerificationRequest?: any;
};

type State = {
  fetchingData: boolean;
  showModal: boolean;
  showUploadModal: boolean;
  showResetModal: boolean;
  status: string;
};

class ManualVerificationButtonsContainer extends React.Component<Props, State> {
  state = {
    fetchingData: false,
    showModal: false,
    showUploadModal: false,
    showResetModal: false,
    status: null,
  };

  getInitiateConditionChecks = (): ConditionCheck[] => {
    const { user } = this.props;
    const riskStatus = user?.risk_assessment?.risk_status;
    const manualStatus = user?.manual_verification_request?.status;
    const latestStatus = user?.verification_request?.status;
    const riskMet = riskStatus === "success";
    const statusMet = manualStatus === "pending";
    const notAlreadyVerified = latestStatus !== "verified";
    const notUpdating = !this.state.fetchingData;

    const statusLabel = this.state.status
      ? `transitioning to "${this.state.status}"`
      : "updating";

    return [
      {
        id: "risk-assessment",
        met: riskMet,
        label: `Risk assessment has not succeeded (current: ${riskStatus || "none"}).`,
      },
      {
        id: "manual-status",
        met: statusMet,
        label:
          manualStatus === "error"
            ? 'The manual verification request is in error. Use "Reset manual verification" to set it back to pending first.'
            : `The manual verification request is not pending (current: ${manualStatus || "none"}).`,
      },
      {
        id: "not-verified",
        met: notAlreadyVerified,
        label: `This user is already verified (latest request status: ${latestStatus}).`,
      },
      {
        id: "not-updating",
        met: notUpdating,
        label: `A status update is in progress (${statusLabel}).`,
      },
    ];
  };

  // An errored manual request blocks every other action; resetting it to
  // pending lets review be initiated again. POS re-checks these conditions
  // (and also refuses a user with another open manual request).
  getResetConditionChecks = (): ConditionCheck[] => {
    const { user } = this.props;
    const riskStatus = user?.risk_assessment?.risk_status;
    const manualStatus = user?.manual_verification_request?.status;
    const latestStatus = user?.verification_request?.status;
    const riskMet = riskStatus === "success";
    const statusMet = manualStatus === "error";
    const notAlreadyVerified = latestStatus !== "verified";
    const notUpdating = !this.state.fetchingData;

    return [
      {
        id: "risk-assessment",
        met: riskMet,
        label: `Risk assessment has not succeeded (current: ${riskStatus || "none"}).`,
      },
      {
        id: "manual-status",
        met: statusMet,
        label: `The manual verification request is not in error (current: ${manualStatus || "none"}).`,
      },
      {
        id: "not-verified",
        met: notAlreadyVerified,
        label: `This user is already verified (latest request status: ${latestStatus}).`,
      },
      {
        id: "not-updating",
        met: notUpdating,
        label: "A status update is in progress.",
      },
    ];
  };

  resetButtonHasRequiredAttributes = (): boolean => {
    return this.getResetConditionChecks().every((check) => check.met);
  };

  initiateButtonHasRequiredAttributes = (): boolean => {
    return (
      this.props.user?.risk_assessment?.risk_status === "success" &&
      this.props.user?.manual_verification_request?.status === "pending" &&
      this.props.user?.verification_request?.status !== "verified"
    );
  };

  acceptOrRejectButtonHasRequiredAttributes = (): boolean => {
    return (
      this.props.user?.risk_assessment?.risk_status === "success" &&
      this.props.user?.manual_verification_request?.status === "in_review"
    );
  };

  uploadButtonHasRequiredAttributes = (): boolean => {
    const identityStatus = this.props.user?.identity_verification?.status;
    const identityFailed =
      identityStatus === "rejected" || identityStatus === "error";
    const hasManualRequest = !!this.props.user?.manual_verification_request;

    return (
      !!this.props.user?.external_reference &&
      (identityFailed || hasManualRequest)
    );
  };

  updateManualVerificationRequest = async (status: string) => {
    this.setState({ fetchingData: true, status });
    await this.props.updateManualVerificationRequest({
      variables: {
        status: status,
        externalReference: this.props.user.external_reference,
      },
    });
    this.setState({ fetchingData: false });
    window.location.reload();
  };

  onCloseModal = () => {
    this.setState({ showModal: !this.state.showModal });
  };

  render() {
    const ManualVerificationStatusChangeConfirmationModalProps = {
      status: this.state.status,
      user: this.props.user,
      onClose: this.onCloseModal,
    };

    return (
      <>
        <section
          className="page"
          style={{
            marginLeft: "-20px",
          }}
        >
          <Grid
            container
            spacing={16}
            direction="row"
            justify="flex-start"
            alignItems="flex-start"
          >
            <Grid item>
              <ManualVerificationViewIdPhotoButton user={this.props.user} />
            </Grid>
            <Grid item>
              <ManualVerificationUploadDocumentButton
                onClick={() => {
                  this.setState({ showUploadModal: true });
                }}
                disabled={!this.uploadButtonHasRequiredAttributes()}
                fetchingData={this.state.fetchingData}
              />
            </Grid>
            <Grid item>
              <ManualVerificationInitiateButton
                user={this.props.user}
                onUserChanged={this.props.onUserChanged}
                onClick={() => {
                  this.updateManualVerificationRequest("in_review");
                }}
                disabled={
                  !this.initiateButtonHasRequiredAttributes() ||
                  this.state.fetchingData
                    ? true
                    : false
                }
                fetchingData={this.state.fetchingData}
                conditionChecks={this.getInitiateConditionChecks()}
              />
            </Grid>
            <Grid item>
              <ManualVerificationResetButton
                onClick={() => {
                  this.setState({ showResetModal: true });
                }}
                disabled={!this.resetButtonHasRequiredAttributes()}
                fetchingData={this.state.fetchingData}
                conditionChecks={this.getResetConditionChecks()}
              />
            </Grid>
            <Grid item>
              <ManualVerificationAcceptButton
                user={this.props.user}
                onUserChanged={this.props.onUserChanged}
                onClick={() => {
                  this.setState({ showModal: true });
                  this.setState({ status: "verified" });
                }}
                disabled={
                  !this.acceptOrRejectButtonHasRequiredAttributes() ||
                  this.state.fetchingData
                    ? true
                    : false
                }
                fetchingData={this.state.fetchingData}
              />
            </Grid>
            <Grid item>
              <ManualVerificationRejectButton
                user={this.props.user}
                onUserChanged={this.props.onUserChanged}
                onClick={() => {
                  this.setState({ showModal: true });
                  this.setState({ status: "rejected" });
                }}
                disabled={
                  !this.acceptOrRejectButtonHasRequiredAttributes() ||
                  this.state.fetchingData
                    ? true
                    : false
                }
                fetchingData={this.state.fetchingData}
              />
            </Grid>
            <Grid item>
              <ManualVerificationDownloadAllImagesButton user={this.props.user} />
            </Grid>
          </Grid>
        </section>
        {this.state.showModal && (
          <ManualVerificationStatusChangeConfirmationModal
            {...ManualVerificationStatusChangeConfirmationModalProps}
          />
        )}
        {this.state.showResetModal && (
          <ManualVerificationResetConfirmationModal
            user={this.props.user}
            onClose={() => this.setState({ showResetModal: false })}
          />
        )}
        {this.state.showUploadModal && (
          <ManualVerificationUploadDocumentModal
            user={this.props.user}
            onClose={() => this.setState({ showUploadModal: false })}
          />
        )}
      </>
    );
  }
}

const updateManualVerificationRequest = graphql(
  gql`
    mutation updateManualVerificationRequest(
      $externalReference: String!
      $status: String!
    ) {
      update_latest_verification_request(
        external_reference: $externalReference
        status: $status
      )
    }
  `,
  {
    name: "updateManualVerificationRequest",
  }
);

export default compose<any>(updateManualVerificationRequest)(
  ManualVerificationButtonsContainer
);
